<script setup>
/**
 * Two charts over whatever the ledger is currently showing: where the balance
 * went, and what came in against what went out. They read the same rows the
 * transaction list does, so a search, a kind filter or a focused plan narrows
 * the charts with everything else — and when the rows are a subset, the balance
 * line becomes a running total from zero, because part of your money is not
 * your balance.
 *
 * Privacy mode leaves the shapes alone and takes the figures away.
 */
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import SectionTitle from './ui/SectionTitle.vue'
import { isHidden } from '../stores/sections'
import Chart from './ui/Chart.vue'
import Treemap from './ui/Treemap.vue'
import {
  balanceFromZero,
  balanceTrend,
  canChartByMonth,
  canHideAll,
  chartRange,
  chartsFiltered,
  categoryTreemap,
  chartSubject,
  chartLineSeries,
  chartSeriesFull,
  chartSlotOf,
  viewableCategories,
  incomeSplit,
  spendingSplit,
  chartWindow,
  fmtGlobal,
  hideGlobal,
  chartGrain,
  MAX_CHART_SERIES,
  setChartGrain,
  setDateRange,
  toggleChartAll,
  toggleChartSeries,
  trendBars,
} from '../stores'
import { state } from '../stores/state'
import { t } from '../stores/i18n'
import { round2 } from '../lib/money'
import { endOfMonth, monthLabel, monthName, monthOf, shortDay, startOfMonth } from '../lib/dates'

const money = (v) => fmtGlobal(v, { round: true })
// Axis ticks compact (RSD 1.6M) so they stop eating the plot; values stay exact.
const tick = (v) => fmtGlobal(v, { compact: true })

const byMonth = computed(() => chartWindow.value.grain === 'month')
/** Day numbers alone only say enough while the window is one month long. */
const oneMonth = computed(
  () => monthOf(chartWindow.value.from) === monthOf(chartWindow.value.to),
)

/** A bucket is a month or a day, and both charts label theirs the same way. */
const barLabel = (row) =>
  byMonth.value
    ? monthName(row.month)
    : oneMonth.value
      ? String(Number(row.date.slice(8)))
      : shortDay(row.date)
const pointLabel = (point) =>
  byMonth.value ? monthName(monthOf(point.date)) : shortDay(point.date)

/** What the charts are looking at — the window itself, whatever the grain. */
const window = computed(() => {
  const { from, to } = chartWindow.value
  if (chartRange.value.set) return `${shortDay(from)} — ${shortDay(to)}`
  return oneMonth.value
    ? monthLabel(monthOf(from))
    : `${monthLabel(monthOf(from), { short: true })} — ${monthLabel(monthOf(to), { short: true })}`
})

/**
 * The calendar behind the columns: a hairline and a name wherever a new month
 * starts, or a new year once the buckets are months. Only worth drawing when
 * the window actually crosses one.
 */
const marksFor = (dates) => {
  const starts = byMonth.value
    ? (date) => monthOf(date).endsWith('-01')
    : (date) => date.slice(8) === '01'
  const name = byMonth.value
    ? (date) => date.slice(0, 4)
    : (date) => monthName(monthOf(date))
  const marks = []
  dates.forEach((date, index) => {
    if (index > 0 && starts(date)) marks.push({ index, label: name(date) })
  })
  return marks
}

/**
 * A span dragged across a chart, in that chart's own buckets: a day is itself,
 * a month is all of itself, so dragging in month mode lands on whole months.
 */
const selectRange = (card, { from, to }) => {
  const first = card.dates[from]
  const last = card.dates[to]
  if (!first || !last) return
  setDateRange(
    byMonth.value ? startOfMonth(monthOf(first)) : first,
    byMonth.value ? endOfMonth(monthOf(last)) : last,
  )
}

const empty = computed(() => !balanceTrend.value.length && !trendBars.value.length)

/** No goals in the window means no third bar, rather than an empty legend. */
const saved = computed(() => trendBars.value.some((r) => r.saved > 0))

/** Slot decides both colour and dash, so a tag's line looks the same however
 *  many others are on beside it. */
/**
 * Slot decides colour *and* dash. Eight lines are more than hue can keep apart
 * — the dataviz validator puts the best eight-hue set below the normal-vision
 * floor — so the dash is not decoration, it is the second half of the identity,
 * and the legend mirrors it.
 */
const DASHES = [[], [7, 4], [2, 3], [11, 4, 2, 4], [16, 5], [1, 4], [6, 3, 1, 3], [4, 4]]
const tagLine = (line, values) => ({
  label: line.name,
  values,
  color: `--c-series-${line.slot + 1}`,
  type: 'line',
  dash: DASHES[line.slot],
})
const showAll = computed(() => state.ui.chartAll)
const onChart = (kind, id) => chartSlotOf(kind, id) > -1

/**
 * The two families keep their own rows — a tag and a category are different
 * claims about a transaction and should not read as one list — but they draw
 * from the same eight slots, because that is how many lines can be told apart.
 */
const FAMILIES = computed(() => [
  {
    kind: 'tag',
    title: t('charts.tags'),
    one: t('charts.oneTag'),
    rows: state.tags.map((t) => ({ id: t.id, name: t.name })),
  },
  {
    kind: 'category',
    title: t('charts.categories'),
    one: t('charts.oneCategory'),
    // Goals' virtual categories after the real ones, marked with the goal's
    // icon so they read as what they are.
    rows: viewableCategories.value,
  },
])

const expanded = computed(() => state.ui.chartsExpanded)
const toggle = () => (state.ui.chartsExpanded = !state.ui.chartsExpanded)
const height = computed(() => (expanded.value ? 300 : 132))

const CARDS = computed(() => {
  const labels = trendBars.value.map(barLabel)
  // Every bucket keeps the day it stands for, so a dragged span can be read
  // back as dates.
  const barDates = trendBars.value.map((r) => r.date ?? `${r.month}-01`)
  const pointDates = balanceTrend.value.map((p) => p.date)
  return [
    {
      key: 'balance',
      title: balanceFromZero.value ? t('charts.runningTotal') : t('charts.balance'),
      icon: 'wallet',
      type: 'line',
      labels: balanceTrend.value.map(pointLabel),
      dates: pointDates,
      marks: marksFor(pointDates),
      series: [
        ...(showAll.value
          ? [
              {
                label: balanceFromZero.value ? t('charts.runningTotal') : t('charts.balance'),
                values: balanceTrend.value.map((p) => p.value),
                color: '--c-text',
                fill: true,
              },
            ]
          : []),
        ...chartLineSeries.value.map((line) => tagLine(line, line.balance)),
      ],
      caption: t(balanceFromZero.value ? 'charts.balanceCaptionSubset' : 'charts.balanceCaption', {
        window: window.value,
      }),
    },
    {
      key: 'flow',
      title: t(saved.value ? 'charts.flowSaved' : 'charts.flow'),
      icon: 'repeat',
      type: 'bar',
      labels,
      dates: barDates,
      marks: marksFor(barDates),
      series: [
        ...(showAll.value
          ? [
              { label: t('charts.in'), values: trendBars.value.map((r) => r.income), color: '--c-accent' },
              { label: t('charts.out'), values: trendBars.value.map((r) => r.spent), color: '--c-warn' },
              // Money put by leaves the balance but is not spending, so it
              // stands beside Out rather than inside it — in the goals' purple.
              ...(saved.value
                ? [{ label: t('charts.savedSeries'), values: trendBars.value.map((r) => r.saved), color: '--c-goal' }]
                : []),
            ]
          : []),
        ...chartLineSeries.value.map((line) => tagLine(line, line.moved)),
      ],
      caption: t(saved.value ? 'charts.flowSavedCaption' : 'charts.flowCaption', {
        window: window.value,
      }),
    },
    // Collapsed, the split is two pies side by side, one per direction.
    // Expanded, it is one treemap across the whole width: the room that makes a
    // pie no easier to read is exactly what a treemap needs to name its tiles.
    ...(expanded.value
      ? [treemapCard()]
      : [
          splitCard({
            key: 'out',
            title: t('charts.whereItGoes'),
            caption: t('charts.spendingByCategory'),
            nothing: t('charts.nothingOut'),
            foot: 'out',
            slices: spendingSplit.value,
          }),
          splitCard({
            key: 'in',
            title: t('charts.whereItComesFrom'),
            caption: t('charts.incomeByCategory'),
            nothing: t('charts.nothingIn'),
            foot: 'in',
            slices: incomeSplit.value,
          }),
        ]),
  ]
})

/**
 * Every category at once, in place of the two pies when the charts are
 * expanded: sized by all the money each moved, coloured by which way it went —
 * or, with chips picked, by the chips' own colours, exactly as the pies take
 * them.
 */
const DIRECTIONS = [
  { direction: 'in', key: 'charts.in', color: '--c-accent' },
  { direction: 'out', key: 'charts.out', color: '--c-warn' },
  { direction: 'saved', key: 'charts.savedSeries', color: '--c-goal' },
]
const treemapCard = () => {
  const tiles = categoryTreemap.value
  const picked = chartSubject.value.picks.length > 0
  const title = t('charts.everyCategory')
  const legend = picked
    ? tiles.map((tile) => ({ label: tile.name, color: tile.color }))
    : [
        ...DIRECTIONS.filter((d) => tiles.some((tile) => tile.direction === d.direction)).map((d) => ({
          label: t(d.key),
          color: d.color,
        })),
        ...(tiles.some((tile) => tile.color === '--c-faint')
          ? [{ label: t('charts.uncategorised'), color: '--c-faint' }]
          : []),
      ]
  return {
    key: 'treemap',
    type: 'treemap',
    title: chartSubject.value.all ? title : `${title} · ${subject.value}`,
    icon: 'folder',
    empty: !tiles.length,
    nothing: t('charts.nothingMoved'),
    tiles,
    legend,
    dates: [],
    caption: chartSubject.value.all
      ? t('charts.treemapCaption', { window: window.value })
      : t('charts.treemapCaptionFor', { subject: subject.value, window: window.value }),
  }
}

/** One split as a pie; it only ever appears with the charts collapsed. */
const splitCard = ({ key, title, caption, nothing, foot, slices }) => ({
  key,
  // Named when the chips have narrowed it, so a pie of two categories is never
  // mistaken for a pie of everything.
  title: chartSubject.value.all ? title : `${title} · ${subject.value}`,
  icon: 'filter',
  empty: !slices.length,
  nothing,
  // The hole is the obvious place for the figure the slices divide up.
  centre: { value: money(round2(slices.reduce((sum, s) => sum + s.value, 0))), label: foot },
  type: 'pie',
  height: height.value,
  labels: slices.map((slice) => slice.name),
  dates: [],
  marks: [],
  series: slices.map((slice) => ({ label: slice.name, values: [slice.value], color: slice.color })),
  caption: chartSubject.value.all
    ? t('charts.splitCaption', { caption, window: window.value })
    : t('charts.splitCaptionFor', { caption, subject: subject.value, window: window.value }),
})

/** The chips the breakdown is limited to, said in a line. */
const subject = computed(() => {
  const { names } = chartSubject.value
  return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ')
})


</script>

<template>
  <section class="min-w-0" :class="isHidden('charts') ? '' : 'w-full'" data-section="charts">
    <SectionTitle id="charts" :title="t('charts.title')">
      <template #extra>
        <button
          class="btn-ghost -my-1 !px-1.5 !py-1"
          :aria-expanded="expanded"
          :aria-label="expanded ? t('charts.collapse') : t('charts.expand')"
          @click="toggle"
        >
          <Icon :name="expanded ? 'collapse' : 'expand'" :size="14" />
        </button>
        <!-- One month holds only one month, so there is nothing to group by
             there: the choice appears with the ledger set to all time. -->
        <span class="ml-1 flex items-center gap-0.5 rounded-lg bg-raised p-0.5">
          <button
            v-for="option in [{ v: 'day', l: t('charts.day') }, { v: 'month', l: t('charts.month') }]"
            :key="option.v"
            class="rounded-md px-2 py-0.5 text-[11.5px] font-semibold transition"
            :class="
              chartGrain === option.v
                ? 'bg-surface text-text shadow-sm'
                : 'text-muted hover:text-text disabled:hover:text-muted'
            "
            :aria-pressed="chartGrain === option.v"
            :disabled="option.v === 'month' && !canChartByMonth"
            :title="option.v === 'month' && !canChartByMonth ? t('charts.needAllTime') : ''"
            @click="setChartGrain(option.v)"
          >
            {{ option.l }}
          </button>
        </span>
      </template>
      <span class="text-faint">
        {{ window }}<template v-if="chartsFiltered"> · {{ t('charts.filtered') }}</template>
      </span>
    </SectionTitle>

    <!-- A swipeable row on a phone, side by side from `lg` up — or one above
         the other, full width, once expanded. -->
    <div
      v-if="!isHidden('charts')"
      class="scroll-thin -mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-1
             sm:mx-0 sm:px-0 lg:grid lg:gap-4"
      :class="expanded ? 'lg:grid-cols-1' : 'lg:grid-cols-2'"
    >
      <article
        v-for="card in CARDS"
        :key="card.key"
        class="card snap-center p-4"
        :class="expanded ? 'w-full min-w-full' : 'min-w-[84%] sm:min-w-[19rem] lg:min-w-0'"
        :data-card="card.key"
      >
        <header class="flex items-center gap-2">
          <p class="flex flex-1 items-center gap-1.5 text-[12px] font-medium text-muted">
            <Icon :name="card.icon" :size="14" /> {{ card.title }}
          </p>
        </header>

        <div
          v-if="empty || card.empty"
          class="mt-3 grid place-items-center text-center"
          :style="{ height: `${card.height ?? height}px` }"
        >
          <p class="text-[12.5px] text-faint">{{ card.nothing ?? t('charts.nothingYet') }}</p>
        </div>
        <!-- The same height as the charts beside it, the whole width of the row. -->
        <Treemap
          v-else-if="card.type === 'treemap'"
          class="mt-2"
          :tiles="card.tiles"
          :legend="card.legend"
          :height="card.height ?? height"
          :format="money"
          :masked="hideGlobal"
          :caption="card.caption"
        />
        <Chart
          v-else
          class="mt-2"
          :type="card.type"
          :labels="card.labels"
          :series="card.series"
          :format="money"
          :tick-format="tick"
          :masked="hideGlobal"
          :caption="card.caption"
          :height="card.height ?? height"
          :marks="card.marks"
          :centre="card.centre"
          :selectable="!!card.dates.length"
          @select="selectRange(card, $event)"
        />
      </article>
    </div>

    <!-- One button per tag and per category: on, it draws its own line on both
         charts — its running total on the balance, how much it moved on the
         other. The two stay in separate rows; they share the colours. -->
    <div v-if="!isHidden('charts') && (state.tags.length || viewableCategories.length)" class="mt-2 grid gap-1.5">
      <div class="flex flex-wrap items-center gap-1.5">
        <!-- Everything, and the only way to turn it off is to have a line to
             look at instead. The last one off brings it back. -->
        <button
          class="chip !py-1 !text-[12.5px]"
          :class="{ 'chip-on': showAll }"
          :aria-pressed="showAll"
          :disabled="showAll && !canHideAll"
          :title="showAll && !canHideAll ? t('charts.needALine') : ''"
          @click="toggleChartAll()"
        >
          {{ t('charts.all') }}
        </button>
        <span v-if="chartSeriesFull" class="text-[11.5px] text-faint">
          {{ t('charts.maxLines', { count: MAX_CHART_SERIES }) }}
        </span>
      </div>

      <div
        v-for="family in FAMILIES"
        :key="family.kind"
        class="flex flex-wrap items-center gap-1.5"
      >
        <template v-if="family.rows.length">
          <span class="w-[5.5rem] shrink-0 text-[11px] font-semibold tracking-wide text-faint uppercase">
            {{ family.title }}
          </span>
          <button
            v-for="row in family.rows"
            :key="row.id"
            class="chip !py-1 !text-[12.5px]"
            :class="{ 'chip-on': onChart(family.kind, row.id) }"
            :aria-pressed="onChart(family.kind, row.id)"
            :aria-label="t('charts.chipLabel', { name: row.name, family: family.one })"
            :disabled="chartSeriesFull && !onChart(family.kind, row.id)"
            @click="toggleChartSeries(family.kind, row.id)"
          >
            <span
              class="h-0.5 w-3"
              :style="{
                background: onChart(family.kind, row.id)
                  ? `var(--c-series-${chartSlotOf(family.kind, row.id) + 1})`
                  : 'currentColor',
                opacity: onChart(family.kind, row.id) ? 1 : 0.4,
              }"
            />
            <Icon v-if="row.goal" name="target" :size="12" class="shrink-0 text-goal" />
            {{ row.name }}
          </button>
        </template>
      </div>
    </div>
  </section>
</template>
